<script setup lang="ts">
import { computed, withDefaults } from 'vue'
import AngleDown from '../svg/AngleDown.vue'
import FAEllipsis from '../svg/FAEllipsis.vue'

type Props = {
  modelValue: number,
  isDisabled?: boolean,
  maxVisibleButtons?: number,
  pageSize: number,
  totalItems: number,
}

const props = withDefaults(defineProps<Props>(), {
  maxVisibleButtons: 3
})

// ++++ Computed
const isAtFirstPage = computed(() => {
  return props.modelValue === 1
})

const isAtLastPage = computed(() => {
  return props.modelValue === totalPages.value || totalPages.value === 0
})

const isTooFarFromStart = computed(() => {
  return startPage.value > 2
})

const isTooFarFromEnd = computed(() => {
  if (totalPages.value - props.modelValue <= 2) {
    return false
  }

  const diff = totalPages.value - endPage.value
  return diff >= 2 &&
    props.modelValue + props.maxVisibleButtons <= totalPages.value
})

const maxVisibleButtonCountIsEven = computed(() => {
  return props.maxVisibleButtons % 2 === 0
})

const startPage = computed(() => {
  if (isAtLastPage.value) {
    return Math.max(totalPages.value - props.maxVisibleButtons + 1, 2)
  }

  const startRange = maxVisibleButtonCountIsEven.value && props.modelValue !== totalPages.value - 1
    ? Math.floor(props.maxVisibleButtons / 2) - 1
    : Math.floor(props.maxVisibleButtons / 2)
  return Math.max(props.modelValue - startRange, 2)
})

const endPage = computed(() => {
  if (isAtFirstPage.value) {
    return Math.min(props.maxVisibleButtons, totalPages.value - 1)
  }

  return Math.min(props.modelValue + Math.floor(props.maxVisibleButtons / 2), totalPages.value - 1)
})

type PageLink = {
  name: number,
  isActive: boolean
}

const pageLinks = computed<PageLink[]>((): PageLink[] => {
  const range: PageLink[] = []

  for (
    let i = startPage.value;
    i <= endPage.value;
    i++
  ) {
    range.push({
      name: i,
      isActive: i === props.modelValue
    })
  }

  return range
})

const showLastPageLink = computed(() => {
  return totalPages.value > 1
})

const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.pageSize)
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

// ++++ Méthodes
const moveToSelectedPage = (page: number): void => {
  if (props.isDisabled) {
    return
  }

  emit('update:modelValue', page)
}

const moveToPreviousPage = (step: number = 1): void => {
  if (props.isDisabled) {
    return
  }

  emit('update:modelValue', props.modelValue - step)
}

const moveToNextPage = (step: number = 1): void => {
  if (props.isDisabled) {
    return
  }

  emit('update:modelValue', props.modelValue + step)
}
</script>

<template>
  <nav class="navigation-pagination"
       :aria-label="$t('pagination.pagination')">
    <a :class="{
      'disabled-arrow': isAtFirstPage || isDisabled,
    }"
       :aria-disabled="isDisabled"
       :aria-label="$t('pagination.previous')"
       href="#"
       @click.prevent="moveToPreviousPage()">
      <AngleDown />
    </a>

    <a :class="{
      'disabled-link': isAtFirstPage || isDisabled,
      'faded': isDisabled
    }"
       :aria-current="isAtFirstPage ? 'page' : undefined"
       :aria-disabled="isDisabled"
       href="#"
       @click.prevent="moveToSelectedPage(1)">
      1
    </a>

    <FAEllipsis v-if="isTooFarFromStart"
                class="navigationEllipsis" />

    <!-- Liens dynamiques -->
    <template v-if="pageLinks.length > 0">
      <a v-for="(pageLink, index) in pageLinks"
         :key="index"
         :class="{
          'disabled-link': pageLink.isActive,
          'faded': isDisabled
        }"
         :aria-disabled="isDisabled"
         :aria-current="pageLink.isActive ? 'page' : undefined"
         href="#"
         @click.prevent="moveToSelectedPage(pageLink.name)">
        {{ pageLink.name }}
      </a>
    </template>
    <!-- Fin liens dynamiques-->

    <FAEllipsis v-if="isTooFarFromEnd"
                class="navigationEllipsis" />

    <a v-if="showLastPageLink"
       :class="{
        'disabled-link': isAtLastPage,
        'faded': isDisabled
      }"
       :aria-current="isAtLastPage ? 'page' : undefined"
       href="#"
       @click.prevent="moveToSelectedPage(totalPages)">
      {{ totalPages }}
    </a>

    <a :class="{
      'disabled-arrow': isAtLastPage || isDisabled
    }"
       :aria-label="$t('pagination.next')"
       @click.prevent="moveToNextPage()">
      <AngleDown />
    </a>
  </nav>
</template>

<style lang="scss">
.navigation-pagination {
  display: flex;
  align-items: center;
  gap: var(--gutter-base, 8px);

  inline-size: fit-content;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.5em;
    width: auto;
    aspect-ratio: 1;
    border-radius: var(--border-radius-base, 6px);

    color: inherit;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 150ms ease-in;

    &:hover {
      color: var(--brandContrastHover, oklch(2% 0 0));
      background-color: var(--brandColorHover, oklch(95% 0 0));
    }

    &:first-of-type {
      svg {
        rotate: 90deg;
      }
    }

    &:last-of-type {
      svg {
        rotate: -90deg;
      }
    }
  }

  [class*="disabled"] {
    cursor: default;
    pointer-events: none;
  }

  .disabled-arrow {
    opacity: 0.5;
  }

  [aria-current="page"] {
    color: var(--brandContrast, oklch(98% 0 0));
    background-color: #008265;
  }
}

.navigationEllipsis {
  height: 2em;
  width: auto;
  aspect-ratio: 1;
  fill: currentColor;
}
</style>
